import { CTA, Eyebrow, Title } from "../components/CommonComponents";

export default function Careers({ careers, title }) {
  return (
    <section id="careers" className="relative overflow-hidden bg-white py-20 sm:py-24 lg:py-28">
      {/* subtle background detail */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 hidden h-full w-[28%] border-l border-[#071A2F]/[0.05] lg:block"
      />

      <div className="relative mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">

        {/* =====
            HEADER
         */}

        <header className="grid gap-8 lg:grid-cols-[1.15fr_.65fr] lg:items-end lg:gap-20">
          <div>
            <Eyebrow> Career Pathways</Eyebrow>

            <Title highlight={"Career Opportunities"}>
              After {title}
            </Title>
          </div>

          <p className="max-w-[480px] text-base leading-8 text-[#626D78]">
            Explore professional roles and career directions where the
            knowledge and capabilities developed during your programme may
            be applied.
          </p>
        </header>

        {/* =====
            CAREER LANDSCAPE
         */}

        <div className="mt-14 grid border-y border-[#071A2F]/10 lg:grid-cols-[380px_1fr] xl:grid-cols-[420px_1fr]">

          {/* ===
              LEFT — CAREER INTRO
          ==== */}

          <div className="relative overflow-hidden bg-[#071A2F] p-8 text-white sm:p-10 lg:min-h-[650px] lg:p-11">

            {/* giant decorative arrow */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-12 -right-5 font-['Libre_Caslon_Display',Georgia,serif] text-[190px] font-normal leading-none text-white/[0.035]"
            >
              ↗
            </span>

            <div className="relative flex h-full flex-col justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                  Your Next Chapter
                </span>

                <h3 className="mt-7 font-['Libre_Caslon_Display',Georgia,serif] text-[36px] font-normal leading-[1.13] sm:text-[42px]">
                  Turn what you learn into{" "}
                  <span className="text-[#D5AD57]">
                    where you can go.
                  </span>
                </h3>

                <p className="mt-6 max-w-[320px] text-[15px] leading-7 text-white/60">
                  The programme can support pathways across organisations,
                  industries and emerging professional environments.
                </p>
                <CTA title="Explore Career Service" bg="gold" />
              </div>

              <div className="relative mt-14 border-t border-white/15 pt-7">
                <span className="block text-xs font-bold uppercase tracking-[0.15em] text-white/45">
                  Career possibilities
                </span>

                <div className="mt-4 flex items-end gap-4">
                  <strong className="font-['Libre_Caslon_Display',Georgia,serif] text-[64px] font-normal leading-none text-[#D5AD57]">
                    {String(careers.length).padStart(2, "0")}
                  </strong>

                  <span className="pb-1 text-sm leading-6 text-white/65">
                    potential roles
                    <br />
                    to explore
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ===
              RIGHT — ROLE EXPLORER
          ==== */}

          <div className="bg-white lg:pl-12 xl:pl-16">

            <div className="flex min-h-[86px] items-center justify-between border-b border-[#071A2F]/10">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
                Explore Career Roles
              </span>

              <span className="hidden text-sm text-[#7A8189] sm:block">
                Where could your programme take you?
              </span>
            </div>

            <div>
              {careers.map((career, index) => (
                <article
                  key={career.role}
                  className="
                    group
                    relative
                    grid
                    gap-5
                    border-b
                    border-[#071A2F]/10
                    py-7
                    transition-all
                    duration-300
                    sm:grid-cols-[54px_0.8fr_1.2fr_50px]
                    sm:items-center
                    lg:min-h-[94px]
                  "
                >
                  {/* INDEX */}
                  <span className="text-xs font-bold tracking-[0.1em] text-[#8B1E3F]/50">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  {/* ROLE */}
                  <div>
                    {/* <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-[#A77B2D]">
                      {career.area}
                    </span> */}

                    <h3 className="mt-1.5 font-['Libre_Caslon_Display',Georgia,serif] text-[23px] font-normal leading-tight text-[#071A2F] transition-colors duration-300 group-hover:text-[#8B1E3F]">
                      {career.title}
                    </h3>
                  </div>

                  {/* DESCRIPTION */}
                  <p className="max-w-[460px] text-sm leading-6 text-[#68727C]">
                    {career.roles.map((role, index) => (
                      <span key={index}>
                        {role}
                        {index < career.roles.length - 1 && ", "}
                      </span>
                    ))}
                  </p>

                  {/* ARROW */}
                  <div className="flex sm:justify-end">
                    <span
                      className="
                        grid
                        h-10
                        w-10
                        place-items-center
                        rounded-full
                        border
                        border-[#071A2F]/10
                        text-base
                        text-[#071A2F]
                        transition-all
                        duration-300
                        group-hover:border-[#8B1E3F]
                        group-hover:bg-[#8B1E3F]
                        group-hover:text-white
                      "
                    >
                      ↗
                    </span>
                  </div>

                  {/* hover bar */}
                  <span className="absolute bottom-[-1px] left-0 h-[2px] w-0 bg-[#8B1E3F] transition-all duration-500 group-hover:w-full" />
                </article>
              ))}
            </div>
          </div>
        </div>

        {/* =====
            INDUSTRY PATHWAYS
         */}

        <div className="grid border-b border-[#071A2F]/10 md:grid-cols-[230px_1fr]">
          <div className="border-[#071A2F]/10 py-8 md:border-r md:pr-8">
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
              Career Environments
            </span>
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-4 py-8 md:pl-10">
            {[
              "Private Sector",
              "Public Sector",
              "Start-ups",
              "Consulting",
              "Entrepreneurship",
              "Higher Education",
            ].map((item) => (
              <span
                key={item}
                className="flex items-center gap-3 text-sm font-semibold text-[#071A2F]"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#D5AD57]" />
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* =====
            DISCLAIMER
         */}

        <div className="mt-7 flex items-start gap-4">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#8B1E3F]" />

          <p className="max-w-[850px] text-sm leading-7 text-[#747B84]">
            Career opportunities depend on individual skills, experience,
            programme specialisation, further qualifications and employer
            requirements. The roles shown above are indicative career
            pathways and do not constitute employment guarantees.
          </p>
        </div>
      </div>
    </section>
  );
}